import React, { memo } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Droppable, Draggable } from 'react-beautiful-dnd';
import KanbanCard from './KanbanCard';
import { Typography, IconButton, Tooltip } from '@material-ui/core';
import DragIndicatorIcon from '@material-ui/icons/DragIndicator';
import AddIcon from '@material-ui/icons/Add';

const useStyles = makeStyles(theme => ({
  column: props => ({
    backgroundColor: props.color || '#ebecf0',
    borderRadius: 8,
    minWidth: 272,
    maxWidth: 272,
    padding: theme.spacing(1),
    marginRight: theme.spacing(1),
    display: 'flex',
    flexDirection: 'column',
    flexShrink: 0,
  }),
  columnTitle: {
    marginBottom: 0,
    fontWeight: 'bold',
    fontSize: '0.95rem',
    color: "#D6D6D6",
    flex: 1,
  },
  cardList: {
    flexGrow: 1,
    overflowY: 'auto',
    ...theme.scrollbarStyles,
    maxHeight: 'calc(100vh - 200px)',
  },
  totalValue: {
    fontSize: '0.78rem',
    color: "rgba(255,255,255,0.7)",
    fontWeight: 500,
    marginTop: 2,
  },
  columnHeader: {
    marginBottom: theme.spacing(1),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    flex: 1,
    minWidth: 0,
  },
  headerRight: {
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0,
  },
  dragHandle: {
    cursor: 'grab',
    color: 'rgba(255, 255, 255, 0.7)',
    padding: theme.spacing(0.5),
    '&:hover': { color: 'rgba(255, 255, 255, 1)' },
  },
  addButton: {
    color: 'rgba(255,255,255,0.8)',
    padding: 4,
    '&:hover': { color: '#fff', background: 'rgba(255,255,255,0.15)' },
  },
  columnMeta: {
    display: 'flex',
    alignItems: 'center',
    flex: 1,
    minWidth: 0,
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  columnContent: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
}));

const KanbanColumn = memo(({ id, title, tickets, color, index, updateTicket, isAdmin, onCreateCard, laneOptions }) => {
  const classes = useStyles({ color });

  const totalValue = tickets.reduce((acc, ticket) => {
    return acc + (Number(ticket.valorVenda) || 0);
  }, 0);

  return (
    <Draggable draggableId={id} index={index}>
      {(provided) => (
        <div
          className={classes.column}
          ref={provided.innerRef}
          {...provided.draggableProps}
        >
          <div className={classes.columnHeader}>
            <div className={classes.headerLeft}>
              {isAdmin && (
                <div {...provided.dragHandleProps} className={classes.dragHandle}>
                  <DragIndicatorIcon fontSize="small" />
                </div>
              )}
              <div className={classes.columnMeta}>
                <Typography className={classes.columnTitle}>{title}</Typography>
                <Typography className={classes.totalValue}>
                  {tickets.length} cards · R$ {totalValue.toFixed(2)}
                </Typography>
              </div>
            </div>
            <div className={classes.headerRight}>
              <Tooltip title="Criar card nesta coluna">
                <IconButton
                  size="small"
                  className={classes.addButton}
                  onClick={() => onCreateCard && onCreateCard(id)}
                >
                  <AddIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </div>
          </div>

          <Droppable droppableId={id} type="CARD">
            {(provided) => (
              <div
                className={classes.cardList}
                ref={provided.innerRef}
                {...provided.droppableProps}
              >
                {tickets.map((ticket, index) => (
                  <KanbanCard
                    key={ticket.id}
                    ticket={ticket}
                    index={index}
                    updateTicket={updateTicket}
                    laneOptions={laneOptions}
                  />
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </div>
      )}
    </Draggable>
  );
});

export default KanbanColumn;
